/**
 * MDF-38: the saved views button in a Data Library toolbar. A view stores the
 * tab layout (columns, widths, filters, sort, grouping) for the signed-in user.
 * The toolbar passes the same state and callbacks it gives the other panels.
 */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  Menu,
  Popover,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
  type MantineSize,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Bookmark, Check, MoreHorizontal, Plus } from 'react-feather';
import type { ColumnConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow, FilterFieldConfig, QuickFilterConfig } from '../../common/FilterPanel/FilterPanel';
import type { SortFieldConfig } from '../../common/SortPanel/SortPanel';
import type { GroupFieldConfig, GroupState } from '../../common/GroupPanel/GroupPanel';
import type { SortState } from '../../../utils/sortTasks';
import { getApiErrorMessage } from '../../../services/api/client';
import { dataLibrarySavedViewsApi, type DataLibrarySavedView } from '../../../services/api/dataLibrarySavedViews';
import { mergeColumns } from '../columnState';
import { DataLibraryTabContext, useDataLibraryColumnWidths } from '../useDataLibraryTableLayout';
import {
  VIEW_NAME_MAX,
  isLayoutModified,
  knownSortFields,
  resolveView,
  toViewBody,
  viewNameError,
  type ViewLayout,
} from '../savedViews';
import classes from './SavedViewsMenu.module.css';

export interface SavedViewsMenuProps {
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  filterFields: FilterFieldConfig[];
  activeFilters: ActiveFilterRow[];
  onFilterAdd: (key: string) => void;
  onFilterChange: (key: string, values: string[]) => void;
  onFilterRemove: (key: string) => void;
  quickFilters?: QuickFilterConfig[];
  activeQuickFilters?: string[];
  onQuickFilterToggle?: (key: string) => void;
  sortFields: SortFieldConfig[];
  activeSort: SortState | null;
  onSortChange: (sort: SortState | null) => void;
  groupFields: GroupFieldConfig[];
  activeGroup: GroupState | null;
  onGroupChange: (group: GroupState | null) => void;
  size?: MantineSize;
}

/** The row being edited in the list: a rename, or a delete waiting for a confirm. */
type RowEdit = { id: string; mode: 'rename'; name: string } | { id: string; mode: 'delete' } | null;

const showError = (error: unknown, fallback: string) =>
  notifications.show({ title: 'Saved views', message: getApiErrorMessage(error, fallback), color: 'red' });

export function SavedViewsMenu({
  columns,
  onColumnsChange,
  filterFields,
  activeFilters,
  onFilterAdd,
  onFilterChange,
  onFilterRemove,
  quickFilters = [],
  activeQuickFilters = [],
  onQuickFilterToggle,
  sortFields,
  activeSort,
  onSortChange,
  groupFields,
  activeGroup,
  onGroupChange,
  size = 'sm',
}: SavedViewsMenuProps) {
  const tab = useContext(DataLibraryTabContext);
  const { columnWidths, setColumnWidths } = useDataLibraryColumnWidths();
  const [opened, setOpened] = useState(false);
  const [views, setViews] = useState<DataLibrarySavedView[]>([]);
  const [appliedId, setAppliedId] = useState<string | null>(null);
  const [newName, setNewName] = useState<string | null>(null);
  const [rowEdit, setRowEdit] = useState<RowEdit>(null);
  const [busy, setBusy] = useState(false);
  // The clicked button can unmount, so keep focus in the panel: Escape then closes it.
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Two tabs can share this instance, so a response for the previous tab must not land here.
  const tabRef = useRef(tab);
  tabRef.current = tab;

  useEffect(() => {
    if (!tab) return;
    let stale = false;
    setViews([]);
    setAppliedId(null);
    dataLibrarySavedViewsApi
      .list(tab)
      .then(list => !stale && setViews(list))
      .catch(error => !stale && showError(error, 'Could not load your saved views'));
    return () => {
      stale = true;
    };
  }, [tab]);

  const known = useMemo(
    () => ({
      filterKeys: filterFields.map(f => f.key),
      quickFilterKeys: quickFilters.map(q => q.key),
      sortFields: knownSortFields(sortFields.map(s => s.value), columns),
      groupFields: groupFields.map(g => g.value),
    }),
    [filterFields, quickFilters, sortFields, groupFields, columns],
  );

  const current: ViewLayout = useMemo(
    () => ({
      columns: columns.map(({ id, visible }) => ({ id, visible })),
      widths: columnWidths,
      filters: activeFilters,
      quickFilters: activeQuickFilters,
      sort: activeSort,
      group: activeGroup,
    }),
    [columns, columnWidths, activeFilters, activeQuickFilters, activeSort, activeGroup],
  );

  const applied = views.find(v => v.id === appliedId) ?? null;
  const modified = applied ? isLayoutModified(current, resolveView(applied.body, current, known)) : false;
  const names = views.map(v => v.name);

  const closeEditors = () => {
    setNewName(null);
    setRowEdit(null);
  };

  const apply = (view: DataLibrarySavedView) => {
    const next = resolveView(view.body, current, known);
    for (const f of activeFilters) {
      if (!next.filters.some(n => n.key === f.key)) onFilterRemove(f.key);
    }
    for (const f of next.filters) {
      if (!activeFilters.some(a => a.key === f.key)) onFilterAdd(f.key);
      onFilterChange(f.key, f.values);
    }
    const quickChanges = [...activeQuickFilters, ...next.quickFilters].filter(
      (k, i, all) => all.indexOf(k) === i && activeQuickFilters.includes(k) !== next.quickFilters.includes(k),
    );
    quickChanges.forEach(k => onQuickFilterToggle?.(k));
    onSortChange(next.sort);
    onGroupChange(next.group);
    onColumnsChange(mergeColumns(columns, next.columns));
    setColumnWidths(next.widths);
    setAppliedId(view.id);
    closeEditors();
    setOpened(false);
  };

  const replaceView = (view: DataLibrarySavedView) =>
    setViews(prev =>
      prev.map(v => (v.id === view.id ? view : v)).sort((a, b) => a.name.localeCompare(b.name)),
    );

  /** Runs a request, then applies its result only if the tab did not change meanwhile. */
  const run = async (action: () => Promise<() => void>, fallback: string) => {
    const startTab = tab;
    setBusy(true);
    try {
      const done = await action();
      if (tabRef.current === startTab) done();
    } catch (error) {
      showError(error, fallback);
    } finally {
      setBusy(false);
    }
  };

  const saveNew = () => {
    if (!tab || newName === null || viewNameError(newName, names)) return;
    void run(async () => {
      const view = await dataLibrarySavedViewsApi.create(tab, newName.trim(), toViewBody(current));
      return () => {
        setViews(prev => [...prev, view].sort((a, b) => a.name.localeCompare(b.name)));
        setAppliedId(view.id);
        setNewName(null);
      };
    }, 'Could not save the view');
  };

  const rename = (view: DataLibrarySavedView, name: string) => {
    if (viewNameError(name, names.filter(n => n !== view.name))) return;
    if (name.trim() === view.name) {
      setRowEdit(null);
      return;
    }
    void run(async () => {
      const renamed = await dataLibrarySavedViewsApi.update(view.id, { name: name.trim() });
      return () => {
        replaceView(renamed);
        setRowEdit(null);
      };
    }, 'Could not rename the view');
  };

  const overwrite = (view: DataLibrarySavedView) =>
    void run(async () => {
      const updated = await dataLibrarySavedViewsApi.update(view.id, { body: toViewBody(current) });
      notifications.show({ message: `Updated "${view.name}" with the current layout`, color: 'green' });
      return () => {
        replaceView(updated);
        setAppliedId(view.id);
      };
    }, 'Could not update the view');

  const remove = (view: DataLibrarySavedView) =>
    void run(async () => {
      await dataLibrarySavedViewsApi.delete(view.id);
      return () => {
        setViews(prev => prev.filter(v => v.id !== view.id));
        setAppliedId(id => (id === view.id ? null : id));
        setRowEdit(null);
      };
    }, 'Could not delete the view');

  const newNameError = newName !== null && newName.trim() !== '' ? viewNameError(newName, names) : null;

  return (
    <Popover
      opened={opened}
      onChange={o => {
        setOpened(o);
        if (!o) closeEditors();
      }}
      position="bottom-start"
      withinPortal
      shadow="md"
      trapFocus={false}
    >
      <Popover.Target>
        <Button
          variant="outline"
          color="gray"
          bg="white"
          style={{ borderColor: '#E8E6E3' }}
          size={size}
          leftSection={<Bookmark size={16} />}
          onClick={() => setOpened(o => !o)}
          classNames={{ label: classes.triggerLabel }}
        >
          {applied ? (
            <>
              <span className={classes.triggerName}>View: {applied.name}</span>
              {modified && (
                <span className={classes.modified} title="The layout differs from this view">
                  <span className={classes.modifiedDot} aria-hidden />
                  Modified
                </span>
              )}
            </>
          ) : (
            'Views'
          )}
        </Button>
      </Popover.Target>

      <Popover.Dropdown ref={dropdownRef} tabIndex={-1} className={classes.dropdown}>
        <Stack gap="sm">
          <Text size="sm" fw={600}>
            Saved views
          </Text>

          {views.length === 0 ? (
            <Text size="sm" c="dimmed">
              No saved views on this tab yet. Save the current layout to come back to it.
            </Text>
          ) : (
            <Stack gap={2} className={classes.list}>
              {views.map(view => {
                const edit = rowEdit?.id === view.id ? rowEdit : null;
                if (edit?.mode === 'rename') {
                  const error = viewNameError(edit.name, names.filter(n => n !== view.name));
                  return (
                    <Group key={view.id} gap="xs" wrap="nowrap" align="flex-start">
                      <TextInput
                        size={size}
                        className={classes.nameInput}
                        value={edit.name}
                        maxLength={VIEW_NAME_MAX}
                        aria-label="View name"
                        error={edit.name.trim() ? error : null}
                        data-autofocus
                        autoFocus
                        onChange={e => setRowEdit({ ...edit, name: e.currentTarget.value })}
                        onKeyDown={e => {
                          if (e.key === 'Enter') rename(view, edit.name);
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            setRowEdit(null);
                          }
                        }}
                      />
                      <Button size={size} disabled={!!error} loading={busy} onClick={() => rename(view, edit.name)}>
                        Save
                      </Button>
                      <Button size={size} variant="subtle" color="gray" onClick={() => setRowEdit(null)}>
                        Cancel
                      </Button>
                    </Group>
                  );
                }
                if (edit?.mode === 'delete') {
                  return (
                    <Group key={view.id} gap="xs" wrap="nowrap" justify="space-between" className={classes.confirm}>
                      <Text size="sm" className={classes.viewName}>
                        Delete "{view.name}"?
                      </Text>
                      <Group gap="xs" wrap="nowrap">
                        <Button size="xs" color="red" loading={busy} onClick={() => remove(view)}>
                          Delete
                        </Button>
                        <Button size="xs" variant="subtle" color="gray" onClick={() => setRowEdit(null)}>
                          Cancel
                        </Button>
                      </Group>
                    </Group>
                  );
                }
                return (
                  <Group key={view.id} gap={4} wrap="nowrap" className={classes.row}>
                    <UnstyledButton className={classes.option} onClick={() => apply(view)}>
                      <Box className={classes.check}>{view.id === appliedId && <Check size={14} />}</Box>
                      <Text size="sm" className={classes.viewName}>
                        {view.name}
                      </Text>
                    </UnstyledButton>
                    <Menu position="bottom-end" withinPortal={false} shadow="md">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" size="md" aria-label={`Actions for ${view.name}`}>
                          <MoreHorizontal size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => setRowEdit({ id: view.id, mode: 'rename', name: view.name })}>
                          Rename
                        </Menu.Item>
                        <Menu.Item onClick={() => overwrite(view)}>Update with current layout</Menu.Item>
                        <Menu.Item color="red" onClick={() => setRowEdit({ id: view.id, mode: 'delete' })}>
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                );
              })}
            </Stack>
          )}

          <Divider />

          {newName === null ? (
            <Button
              variant="subtle"
              color="gray"
              size={size}
              leftSection={<Plus size={16} />}
              className={classes.saveButton}
              onClick={() => setNewName('')}
            >
              Save current layout as a view
            </Button>
          ) : (
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <TextInput
                size={size}
                className={classes.nameInput}
                placeholder="View name"
                value={newName}
                maxLength={VIEW_NAME_MAX}
                aria-label="New view name"
                error={newNameError}
                autoFocus
                onChange={e => setNewName(e.currentTarget.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveNew();
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setNewName(null);
                  }
                }}
              />
              <Button size={size} disabled={!!viewNameError(newName, names)} loading={busy} onClick={saveNew}>
                Save
              </Button>
            </Group>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

export default SavedViewsMenu;
